import { useEffect } from 'react';
import { CUSTOMER_TYPE } from '../../constants/customerType';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import CustomerSelector from '../CustomerSelector';
import { PageContainer, PageContent } from './styles';

/**
 * Componente wrapper que exibe o CustomerSelector antes do conteúdo da página.
 * Deve ser usado nas páginas que precisam que a empresa de transporte escolha
 * entre gerenciar dados próprios ou de clientes.
 *
 * O conteúdo só é exibido após uma seleção válida.
 *
 * @param {React.ReactNode} children - Conteúdo da página
 * @param {string} [selectorFirstOptionLabel] - Label da primeira opção do seletor (ex.: "Todas as empresas"). Se vazio/nulo, usa o padrão "Minha Empresa"
 * @param {string} [selectorFirstOptionDescription] - Descrição da primeira opção (ex.: "Gerenciar todas as entregas em aberto"). Se vazio/nulo, usa o padrão
 * @param {boolean} [selectorClientOnly] - Quando true, o seletor exibe apenas a lista de clientes (sem opção "Todas as empresas"). Usado ex.: Nova entrega sem empresa selecionada.
 * @param {boolean} [includePhysicalCustomers] - Quando true, o seletor lista PJ e PF.
 */
export default function PageWithCustomerSelector({
    children,
    selectorFirstOptionLabel,
    selectorFirstOptionDescription,
    selectorClientOnly = false,
    includePhysicalCustomers = false,
}) {
    const {
        selectedCustomerId,
        selectedCustomerType,
        isSelectorOpen,
        isTransportCompany,
        clearSelection,
    } = useCustomerSelector();

    useEffect(() => {
        if (!isTransportCompany) return;
        if (includePhysicalCustomers) return;
        if (selectedCustomerType === CUSTOMER_TYPE.PESSOA_FISICA && selectedCustomerId) {
            clearSelection();
        }
    }, [
        isTransportCompany,
        includePhysicalCustomers,
        selectedCustomerType,
        selectedCustomerId,
        clearSelection,
    ]);

    if (!isTransportCompany) {
        return <PageContent>{children}</PageContent>;
    }

    return (
        <PageContainer $centered={!selectedCustomerId || isSelectorOpen}>
            <CustomerSelector
                firstOptionLabel={selectorFirstOptionLabel}
                firstOptionDescription={selectorFirstOptionDescription}
                clientOnly={selectorClientOnly}
                includePhysicalCustomers={includePhysicalCustomers}
            />
            {selectedCustomerId && !isSelectorOpen && <PageContent>{children}</PageContent>}
        </PageContainer>
    );
}
